/**
 * ticketModel.ts — IR (representación intermedia) ÚNICA del contenido del ticket BLE.
 *
 * `buildTicketModel(order, config): TicketDoc` centraliza TODO el contenido, los
 * condicionales y las etiquetas del ticket en UNA sola copia. Es PURO: sin DOM, sin
 * bytes, sin I/O de browser → testeable de forma aislada. De este IR cuelgan emitters
 * delgados que SOLO materializan (prohibido agregar contenido fuera del IR):
 *   - emitEscPos(doc, sizeCfg)  → Uint8Array (bytes, en buildWebBleTicket.ts)
 *   - emitTextPreview(doc)      → WebBleTextLayoutLine[] (preview "Solo texto", textLayout.ts)
 *   - emitCanvas(doc, opts)     → HTMLCanvasElement (preview/print imagen — paso futuro)
 *
 * BYTE-SAFETY (RESTRICCIÓN DURA): los bytes del print de texto están VALIDADOS EN
 * HARDWARE (PT-220). El IR captura la SECUENCIA EXACTA de operaciones del encoder
 * (align/bold/size/line/row/sep/feed/cut, EN ORDEN) que hoy emiten buildNormalTicket/
 * buildSimplifiedTicket. emitEscPos reproduce esa secuencia comando-a-comando, por lo
 * que `emitEscPos(buildTicketModel(order, cfg))` es BYTE-IDÉNTICO a la salida legacy de
 * `buildWebBleTicket`. Un golden de equivalencia byte-a-byte (ticketModel.bytes.test.ts)
 * lo congela sobre una matrix amplia que ejercita todas las ramas.
 *
 * MODELO DE CONTENIDO: bold/align/size se modelan como BLOQUES de cambio de estado
 * explícitos (no como flags por línea) para reproducir el patrón real del encoder
 * (`enc.bold(true).line(a).line(b).bold(false)` emite ESC E 1 una sola vez). Así el IR
 * no inventa comandos redundantes y la equivalencia es exacta.
 *
 * computeOrderTotal / paymentLabel / LEGAL_DISCLAIMER viven AQUÍ (única copia); hoy
 * estaban además redefinidos en canvasTicket.ts. buildWebBleTicket.ts y textLayout.ts
 * los re-exportan desde aquí para no romper imports existentes.
 */

import { OrderDetail, ProductExtra, ProductOrder } from '@interfaces/OrderTypes';
import { DELIVERY_MAP, ORDER_PAYMENT_MAP, TICKET_TYPE_MAP } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import { normalizePaymentMethod } from '@pages/Orders/utils/paymentUtils';
import { formatScheduledDateTime } from '@pages/Orders/utils/scheduledOrderUtils';
import {
  CAJA_PRESET,
  COCINA_PRESET,
  presetForRole,
  type TicketContentFlags,
  type WebBleTicketType,
} from '@store/slices/webBlePrinters.types';
import type { EscPosAlign, EscPosSize } from './escpos';

// Re-export para consumidores del IR (presets + tipo de flags viven en el slice durable).
export { CAJA_PRESET, COCINA_PRESET, presetForRole };
export type { TicketContentFlags };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de dominio (única copia). Se re-exportan desde buildWebBleTicket.ts y
// textLayout.ts para retro-compat de imports, y reemplazan las copias locales que
// canvasTicket.ts redefinía (L60/L115/L120).
// ─────────────────────────────────────────────────────────────────────────────

// Disclaimer legal del footer del ticket de caja. Literal único.
export const LEGAL_DISCLAIMER =
  'Este formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son validos hasta la fecha indicada.';

// Total que se IMPRIME ("Total a pagar"): subtotal − descuento + propina + envío.
// El "Cambio" se calcula contra ESTE total (no contra pedido.total del servidor).
export function computeOrderTotal(pedido: OrderDetail['pedido']): number {
  return (pedido.subtotal || 0) - (pedido.coupon_amount || 0) + (pedido.propina || 0) + (pedido.costo_envio || 0);
}

// Etiqueta del método de pago, replicando la lógica del ticket Electron.
export function paymentLabel(order: OrderDetail): string {
  let label: string = ORDER_PAYMENT_MAP[order.pedido.metodo_pago]?.label || order.pedido.metodo_pago;
  if (normalizePaymentMethod(order.pedido.metodo_pago) === ORDER_PAYMENT_MAP.toConfirm.key) {
    label = order.pedido.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en linea';
  }
  return label;
}

// ─────────────────────────────────────────────────────────────────────────────
// TicketDoc / Block — IR neutro, ordenado.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rol de tamaño que SOLO el renderer de imagen (emitCanvas) honra. La térmica colapsa
 * todos los roles a 2 escalones (bold + doble-alto); por eso emitEscPos/emitTextPreview
 * IGNORAN este hint. Las keys coinciden con PRINT_TEXT_SIZES_VALUES_MAP del ticket canvas.
 */
export type CanvasRole = 'title' | 'subTitle' | 'content' | 'medium' | 'small';

/**
 * Hint de presentación EXCLUSIVO de emitCanvas (imagen). emitEscPos/emitTextPreview NO lo
 * leen, por lo que es BYTE-INERTE: agregarlo a un bloque no cambia los bytes ni el preview
 * de texto (el golden de equivalencia lo prueba). Permite que el modo imagen mantenga su
 * salida visual histórica (roles por elemento, align propio, totales como filas etiqueta/
 * valor, extras indentados) mientras DERIVA del MISMO IR — sin reimplementar contenido.
 *
 *  - `role`     : tamaño por elemento (title/subTitle/content/medium/small).
 *  - `align`    : alineación propia del canvas (puede diferir del align de bytes: p.ej. el
 *                 método de entrega va izquierda en bytes pero el canvas lo centra/justifica).
 *  - `asRow`    : el canvas pinta esta `line` como fila "etiqueta … valor" en vez de texto
 *                 plano (totales). `left`/`right` ya separados; `indent` en px.
 *  - `skip`     : el canvas OMITE esta línea (la genera por otra vía, p.ej. extras que el
 *                 canvas pinta como `row` indentado en vez de la línea en-línea de bytes).
 */
export type CanvasHint = {
  role?: CanvasRole;
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  asRow?: { left: string; right: string; indent?: number };
  skip?: boolean;
  /** Texto propio del canvas cuando difiere del de bytes (p.ej. instrucciones de producto:
   *  bytes lleva sangría "  {instr}", el canvas la pinta sin espacios). */
  text?: string;
};

/**
 * Bloque semántico del ticket. La lista es ORDENADA: el orden ES el orden de emisión.
 *
 * `align` / `bold` / `size` son cambios de estado EXPLÍCITOS (espejo de ESC a / ESC E /
 * GS !): el contenido se emite con el estado vigente, igual que el encoder. Esto permite
 * reproducir byte-a-byte el patrón "set una vez, varias líneas, restaurar".
 *
 * `canvas` (opcional, en `line`/`sep`/`feed`) es un hint que SOLO emitCanvas honra; los
 * emitters de bytes y texto lo IGNORAN → byte-inerte (probado por el golden). Es la pieza
 * que permite al modo imagen conservar su salida visual histórica derivando del mismo IR.
 */
export type Block =
  // Logo raster (GS v 0). El raster lo resuelve el caller (useWebBlePrint) FUERA del doc
  // para mantener buildTicketModel puro; emitEscPos lo recibe vía sizeCfg.logoRaster.
  | { kind: 'logo' }
  // Cambio de alineación: 0=izq, 1=centro, 2=der (ESC a n).
  | { kind: 'align'; n: EscPosAlign }
  // Cambio de negrita on/off (ESC E n).
  | { kind: 'bold'; on: boolean }
  // Cambio de tamaño (GS ! n). `role` documenta la intención semántica (título vs cuerpo)
  // para que emitCanvas pueda mapear a px; emitEscPos/emitTextPreview solo usan `mode`.
  | { kind: 'size'; mode: EscPosSize; role?: 'title' | 'body' }
  // Línea de texto + LF, emitida con el estado vigente. `canvas` = hint de imagen (byte-inerte).
  | { kind: 'line'; text: string; canvas?: CanvasHint }
  // Fila "etiqueta ...... valor": valor a la derecha si cabe en `width`; si no, etiqueta
  // arriba y valor align 2 debajo (espejo exacto del helper row() del encoder). `canvas` =
  // hint de imagen (role/indent del row) — byte-inerte.
  | { kind: 'row'; left: string; right: string; width: number; canvas?: CanvasHint }
  // Separador conservador (columnas−1 guiones) — solo si decoration ≠ false.
  | { kind: 'sep' }
  // n líneas en blanco (LF crudos). `canvas` puede mapear el feed a una altura propia.
  | { kind: 'feed'; n: number; canvas?: { height?: number } }
  // Avance de papel + corte (feed n, GS V 0).
  | { kind: 'cut'; feed: number };

/** Documento de ticket: lista ordenada de bloques + metadatos de presentación. */
export type TicketDoc = {
  blocks: Block[];
};

// ─────────────────────────────────────────────────────────────────────────────
// TicketContentConfig — entrada declarativa de contenido.
//
// Subconjunto del perfil persistido (WebBlePrintSettings) que decide QUÉ contenido
// lleva el ticket. Los presets caja/cocina INICIALIZAN esta config (no ramifican
// builders). Por ahora el builder deriva todo el contenido del rol (ticketType) +
// flags existentes para garantizar paridad byte-a-byte con el legacy; los flags
// semánticos ricos (showPrices/showTotals/…) se cablean en pasos posteriores SIN
// cambiar la salida actual de los presets caja/cocina.
// ─────────────────────────────────────────────────────────────────────────────

/** Config de contenido que consume buildTicketModel. Equivalente 1:1 a las opciones que
 *  hoy lee buildWebBleTicket, expresada de forma neutra al renderer. */
export type TicketContentConfig = {
  /** Rol/preset: 'normal' (caja, con precios/totales) | 'simplified' (cocina, sin precios). */
  ticketType: keyof typeof TICKET_TYPE_MAP;
  /**
   * Flags de contenido RESUELTOS (showPrices/showTotals/showTip/…). OPCIONAL: cuando es
   * `undefined`, el builder usa el PRESET del rol (presetForRole) — por lo que un caller
   * que no los pasa obtiene la salida histórica byte-validada (caja/cocina hardcodeados).
   * Cuando se pasan (UI rica), el builder los HONRA sobre el mismo pipeline. Así "sin
   * precios" deja de ser un builder aparte y pasa a ser showPrices=false.
   */
  content?: TicketContentFlags;
  /** Etiqueta del campo de referencias de dirección. */
  referencesLabel?: string;
  /** Datos del comercio para el footer. */
  commerceName?: string;
  commerceAddress?: string;
  /** Imprimir el celular del cliente. Default true (solo caja). */
  showClientPhone?: boolean;
  /** Imprimir el NOMBRE del comercio. Default true. Aplica a caja Y cocina. */
  showCommerceName?: boolean;
  /** Imprimir la DIRECCIÓN del comercio. Default true. Solo caja. */
  showCommerceAddress?: boolean;
  /** Si el logo se resolvió (raster disponible) → emite el bloque {logo}. El raster en sí
   *  lo materializa el emitter de bytes; el IR solo sabe "hay logo". */
  hasLogo?: boolean;
  /** Tamaño de título (modo ESC/POS) — lo decide el caller (titleSize). */
  titleSizeMode: EscPosSize;
  /** Tamaño de cuerpo (modo ESC/POS) — lo decide el caller (bodySize). */
  bodySizeMode: EscPosSize;
  /** Ancho de columna para wrap de productos/filas (32 @58mm, 48 @80mm). */
  bodyWidth: number;
  /** Si false, omite separadores decorativos. */
  decoration?: boolean;
  /** Líneas en blanco antes del corte. */
  feedBeforeCut: number;
  /**
   * Texto del AVISO LEGAL del footer (solo caja). OPCIONAL: si se omite o queda vacío, se usa
   * `LEGAL_DISCLAIMER` (el default) → un caller que no lo pasa obtiene la salida histórica
   * byte-validada. Editable por el usuario (no vive en la BD); el caller pasa el override.
   */
  legalDisclaimer?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Constructor del IR. Mover aquí TODO el contenido/condicionales/etiquetas que hoy
// viven en buildNormalTicket/buildSimplifiedTicket. El ORDEN de los pushes ES el
// orden de emisión del encoder.
// ─────────────────────────────────────────────────────────────────────────────

// Helpers de push tipados (legibilidad). `canvas` es el hint byte-inerte de imagen.
const pushLine = (b: Block[], text: string, canvas?: CanvasHint): void => {
  b.push(canvas ? { kind: 'line', text, canvas } : { kind: 'line', text });
};
const pushAlign = (b: Block[], n: EscPosAlign): void => {
  b.push({ kind: 'align', n });
};
const pushBold = (b: Block[], on: boolean): void => {
  b.push({ kind: 'bold', on });
};
const pushSep = (b: Block[], config: TicketContentConfig): void => {
  if (config.decoration === false) return;
  b.push({ kind: 'sep' });
};

// Resuelve los flags de contenido del config:
//  - Si el caller pasa `content` (UI rica), se honra TAL CUAL.
//  - Si no (golden/legacy path vía buildTicketConfig), se parte del PRESET del rol y se
//    aplican ENCIMA los tres flags legacy que SIEMPRE existieron como opciones de ticket
//    (showClientPhone/showCommerceName/showCommerceAddress, default histórico true → solo
//    cambian si el caller los pasó false). Así la salida queda byte-idéntica a la legacy,
//    que leía esos tres con la convención `!== false`.
const flagsOf = (config: TicketContentConfig): TicketContentFlags => {
  if (config.content) return config.content;
  const preset = presetForRole(config.ticketType as WebBleTicketType);
  return {
    ...preset,
    showClientPhone: config.showClientPhone !== false && preset.showClientPhone,
    showCommerceName: config.showCommerceName !== false && preset.showCommerceName,
    showCommerceAddress: config.showCommerceAddress !== false && preset.showCommerceAddress,
  };
};

// Extras CON precio (caja). Espejo de writeExtras (bytes en-línea). El hint de imagen pinta
// cada extra como `row` indentado (left "{cant} x {nombre}", right precio) — layout histórico
// del canvas — derivando del MISMO bloque (byte-inerte).
const pushExtras = (b: Block[], extras: ProductExtra[]): void => {
  for (const extra of extras) {
    pushLine(b, `  ${extra.cantidad} x ${extra.nombre}  ${formatToCurrency(extra.precio)}`, {
      role: 'content',
      bold: false,
      asRow: { left: `${extra.cantidad} x ${extra.nombre}`, right: formatToCurrency(extra.precio), indent: 16 },
    });
  }
};

// Extras SIN precio (cocina). Espejo de writeExtrasSimplified. El canvas lo pinta como texto
// izquierda normal (mismo string, sin indent extra: ya trae el prefijo "  + ").
const pushExtrasSimplified = (b: Block[], extras: ProductExtra[]): void => {
  for (const extra of extras) {
    pushLine(b, `  + ${extra.cantidad} x ${extra.nombre}`, { role: 'content', align: 'left', bold: false });
  }
};

// Productos con precios (caja). Espejo de writeProducts. `flags.extrasShowPrice` decide si
// los extras llevan precio; con el preset caja (true) la salida es byte-idéntica al legacy.
const pushProducts = (
  b: Block[],
  products: ProductOrder[],
  config: TicketContentConfig,
  flags: TicketContentFlags,
): void => {
  const width = config.bodyWidth;
  for (const product of products) {
    const name = `${product.cantidad} x ${product.nombre} - ${product.presentacion}`;
    pushBold(b, true);
    b.push({
      kind: 'row',
      left: name,
      right: formatToCurrency(product.subtotal),
      width,
      canvas: { role: 'content', bold: true },
    });
    pushBold(b, false);
    if (product.instrucciones) {
      // Bytes: "  {instr}"; canvas: texto sin el sangrado de espacios (lo da el render).
      pushLine(b, `  ${product.instrucciones}`, {
        role: 'content',
        align: 'left',
        bold: true,
        text: product.instrucciones,
      });
    }
    // Extras: con o sin precio según el flag (caja default = con precio → byte-idéntico).
    if (flags.extrasShowPrice) pushExtras(b, product.extras);
    else pushExtrasSimplified(b, product.extras);
    pushSep(b, config);
  }
};

// Productos sin precios (cocina). Espejo de writeProductsSimplified.
const pushProductsSimplified = (b: Block[], products: ProductOrder[], config: TicketContentConfig): void => {
  for (const product of products) {
    pushBold(b, true);
    pushLine(b, `${product.cantidad} x ${product.nombre} - ${product.presentacion}`, {
      role: 'content',
      align: 'left',
      bold: true,
    });
    pushBold(b, false);
    if (product.instrucciones) {
      pushLine(b, `  Instrucciones: ${product.instrucciones}`, { role: 'content', align: 'left', bold: true });
    }
    pushExtrasSimplified(b, product.extras);
    pushSep(b, config);
  }
};

// Bloque de totales (caja). Espejo de writeFooterTotals. Bytes: líneas "Etiqueta: $x" align 2.
// Canvas (hint asRow): fila etiqueta-izq / valor-der SIN dos puntos — layout histórico del
// canvas, derivado del mismo bloque. Cada `line` lleva su asRow con left/right ya separados.
const pushFooterTotals = (b: Block[], order: OrderDetail, flags: TicketContentFlags): void => {
  const { pedido } = order;
  pushAlign(b, 2);
  pushLine(b, `Subtotal: ${formatToCurrency(pedido.subtotal || 0)}`, {
    role: 'content',
    bold: false,
    asRow: { left: 'Subtotal', right: formatToCurrency(pedido.subtotal || 0) },
  });
  if (pedido.coupon_amount) {
    pushLine(b, `Descuento: -${formatToCurrency(pedido.coupon_amount)}`, {
      role: 'content',
      bold: false,
      asRow: { left: 'Descuento', right: `-${formatToCurrency(pedido.coupon_amount)}` },
    });
  }
  // Propina: gated por showTip (caja default = true → byte-idéntico). El monto total SIEMPRE
  // incluye la propina (computeOrderTotal), solo se omite la LÍNEA si showTip=false.
  if (flags.showTip && pedido.propina) {
    pushLine(b, `Propina: ${formatToCurrency(pedido.propina)}`, {
      role: 'content',
      bold: false,
      asRow: { left: 'Propina', right: formatToCurrency(pedido.propina) },
    });
  }
  if (pedido.metodo_entrega === 'delivery') {
    pushLine(b, `Costo de envio: ${formatToCurrency(pedido.costo_envio || 0)}`, {
      role: 'content',
      bold: false,
      asRow: { left: 'Costo de envio', right: formatToCurrency(pedido.costo_envio || 0) },
    });
  }
  const total = computeOrderTotal(pedido);
  pushBold(b, true);
  pushLine(b, `Total a pagar: ${formatToCurrency(total)}`, {
    role: 'content',
    bold: true,
    asRow: { left: 'Total a pagar', right: formatToCurrency(total) },
  });
  pushBold(b, false);
  pushAlign(b, 0);
};

// Bloque de domicilio (caja, solo delivery). Espejo de writeDeliveryAddress.
const pushDeliveryAddress = (b: Block[], order: OrderDetail, referencesLabel: string): void => {
  const { pedido } = order;
  if (pedido.metodo_entrega !== 'delivery') return;
  pushBold(b, true);
  pushLine(b, 'Domicilio de entrega:', { role: 'content', align: 'left', bold: true });
  pushBold(b, false);
  const addrCanvas: CanvasHint = { role: 'content', align: 'left', bold: false };
  if (pedido.colonia) pushLine(b, `Zona o Colonia: ${pedido.colonia}`, addrCanvas);
  if (pedido.calle) pushLine(b, `Calle o Mza: ${pedido.calle}`, addrCanvas);
  if (pedido.numero_exterior) pushLine(b, `Numero Exterior: ${pedido.numero_exterior}`, addrCanvas);
  if (pedido.numero_interior) pushLine(b, `Numero interior: ${pedido.numero_interior}`, addrCanvas);
  if (pedido.direccion_completa) pushLine(b, `Direccion: ${pedido.direccion_completa}`, addrCanvas);
  if (pedido.referencias) pushLine(b, `${referencesLabel}: ${pedido.referencias}`, addrCanvas);
};

// Bloque del REPARTIDOR asignado (solo delivery). El dato llega en `pedido.delivery_driver_info`,
// que el store hidrata por el socket RIDER_ASSIGNED o por el refetch tras asignar un delegado
// (PLI-2031). La etiqueta "Tel:" solo se emite si hay teléfono — si no, quedaría una línea con la
// etiqueta vacía. Condicional a que el flag esté encendido Y haya repartidor → no altera el golden
// de los tickets existentes (el preset nace en false).
const pushDeliveryDriver = (b: Block[], order: OrderDetail): void => {
  const { pedido } = order;
  const driver = pedido.delivery_driver_info;
  if (pedido.metodo_entrega !== 'delivery' || !driver?.name) return;
  pushBold(b, true);
  pushLine(b, `Repartidor: ${driver.name}`, { role: 'content', align: 'left', bold: true });
  pushBold(b, false);
  if (driver.phone) {
    pushLine(b, `Tel: ${driver.phone}`, { role: 'content', align: 'left', bold: false });
  }
};

// Ticket de caja completo. Espejo EXACTO de buildNormalTicket (orden de comandos incluido).
// Cada SECCIÓN se gatea por un flag de contenido; con el preset caja (todo true → flagsOf
// devuelve CAJA_PRESET cuando no hay config.content) la salida es BYTE-IDÉNTICA al legacy.
const buildNormalDoc = (order: OrderDetail, config: TicketContentConfig): Block[] => {
  const { pedido } = order;
  const b: Block[] = [];
  const flags = flagsOf(config);
  const tSize = config.titleSizeMode;
  const bSize = config.bodySizeMode;
  const referencesLabel = config.referencesLabel || 'Referencias';

  if (config.hasLogo && flags.showLogo) b.push({ kind: 'logo' });

  pushAlign(b, 1);
  // Título con su propio tamaño; al terminar se restaura el tamaño del CUERPO (no 0).
  b.push({ kind: 'size', mode: tSize, role: 'title' });
  pushBold(b, true);
  pushLine(b, flags.title, { role: 'title', align: 'center', bold: true });
  pushLine(b, `No. ${pedido.folio}`, { role: 'title', align: 'center', bold: true });
  pushBold(b, false);
  b.push({ kind: 'size', mode: bSize, role: 'body' });
  b.push({ kind: 'feed', n: 1, canvas: { height: 8 } });

  // Leyenda de pedido programado: distingue el ticket de uno ya aceptado (la cocina no debe
  // prepararlo antes de tiempo). Condicional a is_scheduled → no altera el golden de no-programados.
  if (pedido.is_scheduled) {
    pushBold(b, true);
    pushLine(b, 'PEDIDO PROGRAMADO', { role: 'content', align: 'center', bold: true });
    pushLine(
      b,
      `Entregar: ${pedido.scheduled_delivery_time ? formatScheduledDateTime(pedido.scheduled_delivery_time) : ''}`,
      { role: 'content', align: 'center', bold: true },
    );
    pushBold(b, false);
    b.push({ kind: 'feed', n: 1, canvas: { height: 8 } });
  }

  if (flags.showClientName && pedido.nombre) {
    pushBold(b, true);
    pushLine(b, 'Cliente:', { role: 'content', align: 'center', bold: true });
    pushLine(b, pedido.nombre, { role: 'subTitle', align: 'center', bold: true });
    pushBold(b, false);
  }
  if (flags.showClientPhone && pedido.numero_celular) {
    const code = pedido.country_code ? `+${pedido.country_code} ` : '';
    pushLine(b, `Celular: ${code}${pedido.numero_celular}`, { role: 'medium', align: 'center', bold: true });
  }
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    pushLine(b, `Mesa: ${tableNumber}`, { role: 'content', align: 'center', bold: true });
  }
  b.push({ kind: 'feed', n: 1, canvas: { height: 8 } });

  pushBold(b, true);
  pushLine(b, 'Detalle del pedido:', { role: 'content', align: 'center', bold: true });
  pushBold(b, false);
  pushAlign(b, 0);
  pushSep(b, config);
  // showPrices=false (caja sin precios) → mismo render de productos sin la columna de precio,
  // reutilizando el pipeline simplificado. Con el preset caja (true) es byte-idéntico.
  if (flags.showPrices) pushProducts(b, order.productos, config, flags);
  else pushProductsSimplified(b, order.productos, config);
  if (flags.showTotals) pushFooterTotals(b, order, flags);
  b.push({ kind: 'feed', n: 1, canvas: { height: 8 } });

  if (flags.showComment && pedido.comentario) {
    pushBold(b, true);
    pushLine(b, `Indicaciones: ${pedido.comentario}`, { role: 'content', align: 'left', bold: true });
    pushBold(b, false);
  }
  pushLine(b, `Metodo de entrega: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`, {
    role: 'content',
    align: 'left',
    bold: true,
  });
  pushLine(b, `Tipo de pago: ${paymentLabel(order)}`, { role: 'content', align: 'left', bold: true });

  const isCash = normalizePaymentMethod(pedido.metodo_pago) === ORDER_PAYMENT_MAP.efectivo.key;
  if (pedido.cliente_paga_con && isCash) {
    const paidWith = parseFloat(pedido.cliente_paga_con || '0');
    pushLine(b, `Cliente paga con: ${formatToCurrency(paidWith)}`, { role: 'content', align: 'left', bold: true });
    pushLine(b, `Cambio: ${formatToCurrency(paidWith - computeOrderTotal(pedido))}`, {
      role: 'content',
      align: 'left',
      bold: true,
    });
  }

  if (flags.showDeliveryAddress) pushDeliveryAddress(b, order, referencesLabel);
  if (flags.showDeliveryDriver) pushDeliveryDriver(b, order);

  pushSep(b, config);
  pushAlign(b, 1);
  if (flags.showCommerceName && config.commerceName) {
    pushBold(b, true);
    pushLine(b, config.commerceName, { role: 'content', align: 'center', bold: true });
    pushBold(b, false);
  }
  if (flags.showCommerceAddress && config.commerceAddress)
    pushLine(b, config.commerceAddress, { role: 'small', align: 'center', bold: false });
  if (flags.showDate)
    pushLine(b, `Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`, {
      role: 'small',
      align: 'center',
      bold: true,
    });
  if (flags.showLegalDisclaimer)
    pushLine(b, config.legalDisclaimer?.trim() || LEGAL_DISCLAIMER, { role: 'small', align: 'center', bold: false });

  b.push({ kind: 'cut', feed: config.feedBeforeCut });
  return b;
};

// Comanda de cocina. Espejo EXACTO de buildSimplifiedTicket. Con el preset cocina la salida
// es BYTE-IDÉNTICA al legacy; los flags permiten p.ej. "cocina con comentario apagado".
const buildSimplifiedDoc = (order: OrderDetail, config: TicketContentConfig): Block[] => {
  const { pedido } = order;
  const b: Block[] = [];
  const flags = flagsOf(config);
  const tSize = config.titleSizeMode;
  const bSize = config.bodySizeMode;

  if (config.hasLogo && flags.showLogo) b.push({ kind: 'logo' });

  pushAlign(b, 1);
  b.push({ kind: 'size', mode: tSize, role: 'title' });
  pushBold(b, true);
  pushLine(b, flags.title, { role: 'title', align: 'center', bold: true });
  pushLine(b, `No. ${pedido.folio}`, { role: 'title', align: 'center', bold: true });
  pushBold(b, false);
  b.push({ kind: 'size', mode: bSize, role: 'body' });
  pushLine(b, `Metodo: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`, {
    role: 'content',
    align: 'center',
    bold: true,
  });
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    pushLine(b, `Mesa: ${tableNumber}`, { role: 'content', align: 'center', bold: true });
  }
  b.push({ kind: 'feed', n: 1, canvas: { height: 8 } });

  pushBold(b, true);
  pushLine(b, 'Productos:', { role: 'content', align: 'center', bold: true });
  pushBold(b, false);
  pushAlign(b, 0);
  pushProductsSimplified(b, order.productos, config);

  if (flags.showComment && pedido.comentario) {
    pushBold(b, true);
    pushLine(b, `Indicaciones: ${pedido.comentario}`, { role: 'content', align: 'left', bold: true });
    pushBold(b, false);
  }

  pushSep(b, config);
  pushAlign(b, 1);
  if (flags.showCommerceName && config.commerceName) {
    pushBold(b, true);
    pushLine(b, config.commerceName, { role: 'content', align: 'center', bold: true });
    pushBold(b, false);
  }
  if (flags.showDate)
    pushLine(b, `Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`, {
      role: 'small',
      align: 'center',
      bold: true,
    });

  b.push({ kind: 'cut', feed: config.feedBeforeCut });
  return b;
};

// Construye el IR del ticket a partir de la orden y la config de contenido.
// ÚNICA fuente de contenido/orden para TODOS los emitters (bytes, texto, imagen).
export function buildTicketModel(order: OrderDetail, config: TicketContentConfig): TicketDoc {
  const blocks =
    config.ticketType === TICKET_TYPE_MAP.simplified.key
      ? buildSimplifiedDoc(order, config)
      : buildNormalDoc(order, config);
  return { blocks };
}
